import {objectFromEntries} from '@benchling/js-utils';

import {CoreTableType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {GetAnalyticsDashboard} from 'insights/analytics-dashboards/details/graphql/__generated/GetAnalyticsDashboard';
import {columnNameToDisplayName} from 'insights/warehouse/columnNames';
import {sqlColumnTypeToIcon} from 'insights/warehouse/warehouse-diagram/SqlColumnTypeMappers';
import {WarehouseColumnData, WarehouseTableData} from 'insights/warehouse/warehouseModelsDataLayout';
import {getStore} from 'redux-stores/Store';
import {
  GetFullTagSchemaQuery,
  GetFullTagSchemaQueryVariables,
} from 'schemas/__generated/GetFullTagSchemaQuery';
import {GET_FULL_TAG_SCHEMA_QUERY} from 'schemas/useTagSchemaQuery';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {
  DEFAULT_AVAILABLE_COLUMN_STATE,
  POTENTIAL_LINK_COLUMN_TYPES,
  TEXT_COLUMN_TYPE_INFO,
} from './SQLTemplateFormConstants';
import {
  AvailableColumn,
  AvailableColumnType,
  AvailableColumnTypeInfo,
  SQLTemplateFormState,
} from './SQLTemplateFormTypes';

type AnalyticsQuery = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>['analyticsQueries'][number];

/**
 * Get a mapping from field system names to field display names, for a Warehouse table.
 *
 * System names are the fields' names in the Warehouse. Display names come from the corresponding tag schema.
 */
async function getFieldDisplayNamesForSelectedTable(
  selectedTable: WarehouseTableData
): Promise<{[fieldSystemName: string]: string}> {
  if (selectedTable.schema == null) {
    return {};
  }

  const {tagSchema} = await doQuerySafe<GetFullTagSchemaQuery, GetFullTagSchemaQueryVariables>(
    {query: GET_FULL_TAG_SCHEMA_QUERY},
    {variables: {id: selectedTable.schema.id}}
  );

  if (isInaccessible(tagSchema)) {
    return {};
  }

  return objectFromEntries(
    tagSchema.allFieldsJson.map(({systemName, displayName}: {systemName: string; displayName: string}) => [
      systemName,
      displayName,
    ])
  );
}

/**
 * Exported for creating fixtures.
 */
export function buildAvailableColumnsForTable(
  selectedTable: WarehouseTableData,
  fieldDisplayNames: {[fieldSystemName: string]: string}
): Array<AvailableColumn> {
  return selectedTable.columns.map((column) => ({
    ...getDefaultAvailableColumnForColumnData(column),
    ...(fieldDisplayNames[column.name] ? {alias: fieldDisplayNames[column.name]} : null),
    typeInfo: getColumnTypeInfo(column),
  }));
}

export async function getDefaultAvailableColumnsForTable(
  selectedTable: WarehouseTableData
): Promise<Array<AvailableColumn>> {
  const fieldDisplayNames = await getFieldDisplayNamesForSelectedTable(selectedTable);
  return buildAvailableColumnsForTable(selectedTable, fieldDisplayNames);
}

export function getDefaultAvailableColumnForColumnData(column: WarehouseColumnData): AvailableColumn {
  return {
    ...DEFAULT_AVAILABLE_COLUMN_STATE,
    columnData: column,
    alias: columnNameToDisplayName(column.name),
  };
}

// Should only be used outside of component render functions. They should instead use DataConnector to
// be able to re-render with state updates.
export function getAnalyticsQuerySQLTemplateState(
  analyticsQueryAttrs: Pick<AnalyticsQuery, 'id'>
): SQLTemplateFormState | null {
  return (
    getStore().getState().ui.insights.blockEditorUIs[analyticsQueryAttrs.id]?.sqlTemplateFormState ?? null
  );
}

export function getColumnDisplayName(column: AvailableColumn): string {
  return column.alias ?? column.columnData.name;
}

export function getColumnIcon(availableColumn: AvailableColumn): BenchlingIconName | null {
  if (
    availableColumn.typeInfo.type === AvailableColumnType.BIOENTITY &&
    availableColumn.typeInfo.linkedSchema != null
  ) {
    return availableColumn.typeInfo.linkedSchema.iconId as BenchlingIconName;
  }

  return sqlColumnTypeToIcon(availableColumn.columnData.type);
}

export function getColumnTypeInfo(columnData: WarehouseColumnData): AvailableColumnTypeInfo {
  function columnIsBioentityId(column: WarehouseColumnData): boolean {
    return column.name === 'id' && column.table.coreTableType === CoreTableType.ENTITY;
  }

  if (!POTENTIAL_LINK_COLUMN_TYPES.includes(columnData.type)) {
    return TEXT_COLUMN_TYPE_INFO;
  }

  let entityTable;
  if (columnIsBioentityId(columnData)) {
    entityTable = columnData.table;
  } else {
    entityTable = columnData.relationships.find((relationship) => columnIsBioentityId(relationship.target))
      ?.target.table;
  }
  return entityTable != null
    ? {
        type: AvailableColumnType.BIOENTITY,
        linkedSchema: entityTable.schema?.__typename === 'TagSchemaNode' ? entityTable.schema : null,
      }
    : TEXT_COLUMN_TYPE_INFO;
}

export function validateFormValues(
  values: Array<string>,
  fieldDescriptor: string,
  forbiddenStrings: Array<string>
): string | null {
  const validationErrors = values
    .map((value) => {
      let validationError = null;
      forbiddenStrings.forEach((forbiddenString) => {
        if (value.includes(forbiddenString)) {
          validationError = `${fieldDescriptor} cannot include ${forbiddenString}`;
        }
      });

      return validationError;
    })
    .filter((validationError) => validationError != null);

  return validationErrors.length > 0 ? validationErrors[0] : null;
}
